import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAdmin } from "@/hooks/useAdmin";
import {
  LayoutDashboard,
  FileText,
  Users,
  Package,
  LogOut,
  Menu,
  X,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import logoAms from "@/assets/logo-ams-white.png";

interface AdminLayoutProps {
  children: React.ReactNode;
}

const navItems = [
  { href: "/admin", label: "Tableau de bord", icon: LayoutDashboard },
  { href: "/admin/devis", label: "Demandes de devis", icon: FileText },
  { href: "/admin/produits", label: "Produits", icon: Package },
  { href: "/admin/contacts", label: "Contacts & Prospects", icon: Users },
];

const AdminLayout = ({ children }: AdminLayoutProps) => {
  const { isAdmin, loading, user, logout } = useAdmin();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary/30">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!isAdmin) return null;

  const isActive = (path: string) => {
    if (path === "/admin") return location.pathname === "/admin";
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-secondary/30 flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-accent text-accent-foreground transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-5 border-b border-accent-foreground/10">
            <Link to="/" className="flex items-center gap-3">
              <img src={logoAms} alt="AMS DISTRIB" className="h-10 w-auto bg-white rounded p-1" />
              <span className="text-sm font-semibold opacity-70">Admin</span>
            </Link>
          </div>

          {/* Nav */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  isActive(item.href)
                    ? "bg-primary text-primary-foreground"
                    : "text-accent-foreground/70 hover:bg-accent-foreground/10 hover:text-accent-foreground"
                }`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
                {isActive(item.href) && <ChevronRight className="w-4 h-4 ml-auto" />}
              </Link>
            ))}
          </nav>

          {/* User info & logout */}
          <div className="p-4 border-t border-accent-foreground/10 space-y-3">
            <p className="text-xs text-accent-foreground/50 truncate">{user?.email}</p>
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="w-full justify-start text-accent-foreground/70 hover:text-accent-foreground hover:bg-accent-foreground/10"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
            <Link to="/">
              <Button variant="ghost" size="sm" className="w-full justify-start text-accent-foreground/50 hover:text-accent-foreground hover:bg-accent-foreground/10 text-xs">
                ← Retour au site
              </Button>
            </Link>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main content */}
      <div className="flex-1 lg:ml-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border px-4 py-3 lg:px-8">
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-secondary"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <h1 className="text-lg font-semibold text-foreground">
              {navItems.find((item) => isActive(item.href))?.label || "Administration"}
            </h1>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
};

export default AdminLayout;
